// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezoneDayJs from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezoneDayJs);

interface TaskInfoForPDF {
  readonly task_name: string;
  readonly task_time: number;
  readonly crop?: { readonly crop_name: string } | null;
  readonly notes?: string;
}

interface TaskGroupForPDF {
  readonly tasks: readonly TaskInfoForPDF[];
  readonly traysNotRounded: number;
  readonly isCompleted: boolean;
}

export const taskGroupsToStringList = (taskGroups: readonly TaskGroupForPDF[]): readonly TodoInput[] => {
  return taskGroups.map((group) => {
    const task = group.tasks[0];
    const name = task.task_name;
    const trays = Math.ceil(group.traysNotRounded);
    const cropName = task.crop?.crop_name || undefined;
    const notes = task.notes || undefined;

    return {
      taskName: name,
      taskNotes: notes,
      trays,
      cropName,
      taskDate: task.task_time,
      isCompleted: group.isCompleted,
    };
  });
};

interface TodoInput {
  readonly taskName: string;
  readonly taskNotes?: string;
  readonly trays: number; // Some tasks have no crop/trays. If so then it's 0
  readonly cropName?: string; // Some tasks have no crop
  readonly taskDate: number; // Task date in Unix timestamp
  readonly isCompleted: boolean;
}

export const generateTodoPDF = (
  jsPDF: typeof import('jspdf').jsPDF,
  {
    todos,
    title,
    subtitle,
    downloadTitle,
    timezone,
  }: {
    readonly todos: readonly TodoInput[];
    readonly title: string;
    readonly subtitle?: string;
    readonly downloadTitle: string;
    readonly timezone: string;
  },
) => {
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4',
  });

  // Page dimensions and settings
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - 2 * margin;
  const borderRadius = 2; // Border radius
  const lineSpacing = 10; // Spacing between lines
  const notesSpacing = 6; // Spacing between notes and todos
  let startY = subtitle ? 54 : 44; // Initial Y position for the first todo item
  let currentPage = 1; // Track current page number

  const drawBorderAndFooter = () => {
    // Draw a rounded rectangle border
    doc.roundedRect(margin, margin, contentWidth, pageHeight - 2 * margin, borderRadius, borderRadius);
    // Page number
    doc.setFontSize(10);
    doc.text(`Page ${currentPage}`, pageWidth - margin, pageHeight - margin / 2, { align: 'right' });
    // App name
    doc.text('Microgreen Manager', pageWidth / 2, pageHeight - margin / 2, { align: 'center' });
  };

  const addNewPage = () => {
    doc.addPage();
    currentPage += 1;
    startY = currentPage === 1 ? 50 : margin + 10; // Adjust startY for title and date exclusion on subsequent pages
    drawBorderAndFooter(); // Draw border and footer for the new page
  };

  // Draw initial border and footer
  drawBorderAndFooter();

  if (currentPage === 1) {
    // Title and subtitle
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(title, pageWidth / 2, 30, { align: 'center' });
    if (subtitle) {
      doc.setFontSize(12);
      doc.text(subtitle, pageWidth / 2, 40, { align: 'center' });
    }
  }

  const todosGroupedByDate = todos.reduce((acc, todo) => {
    const date = dayjs.unix(todo.taskDate).tz(timezone).format('YYYY-MM-DD');
    if (!acc.has(date)) {
      acc.set(date, [todo]);
    } else {
      acc.get(date)?.push(todo);
    }

    return acc;
  }, new Map<string, TodoInput[]>());
  const sortedDates = Array.from(todosGroupedByDate.keys()).sort((a, b) => dayjs(a).unix() - dayjs(b).unix());

  // Set font for todos
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);

  const formatTodoString = (todo: TodoInput): string => {
    const trays = todo.trays > 0 ? ` - ${todo.trays} trays` : '';
    const crop = todo.cropName ? ` - ${todo.cropName}` : '';

    return `${todo.taskName}${trays}${crop}`;
  };

  for (const date of sortedDates) {
    const dateTodos = todosGroupedByDate.get(date) ?? [];

    if (startY > pageHeight - margin - 10) {
      addNewPage();
    }

    // Print date subheader
    doc.setFontSize(14);
    doc.text(dayjs(date).tz(timezone).format('ddd, MMM D'), margin + 10, startY);
    startY += 8; // Space before listing todos

    dateTodos.forEach((todo, dateIdx) => {
      const splitText = doc.splitTextToSize(formatTodoString(todo), contentWidth - 15) as string[]; // Split text if it overflows
      splitText.forEach((line, lineIndex) => {
        if (startY > pageHeight - margin - 10) {
          addNewPage();
        }
        doc.setFontSize(12);

        // Checkbox for the first line of split text only
        if (lineIndex === 0) {
          doc.rect(20, startY, 5, 5);
        }

        // If the task is completed, mark the checkbox
        if (todo.isCompleted && lineIndex === 0) {
          doc.setFont('zapfdingbats'); // Use the Zapf Dingbats font for the checkmark
          doc.text('4', 20.5, startY + 3.5); // Position the checkmark within the box
          doc.setFont('helvetica'); // Switch back to Helvetica for the text
        }

        // Adjust text start position based on whether it's the first line of split text
        doc.text(line, 30, startY + 4);

        startY += todo.taskNotes ? notesSpacing : lineSpacing; // Increment startY for the next line/todo item or notes
      });

      if (todo.taskNotes) {
        const notesText = doc.splitTextToSize(todo.taskNotes, contentWidth - 25) as string[]; // Split notes text if it overflows
        doc.setFontSize(10); // Slightly smaller text for notes
        notesText.forEach((noteLine, idx) => {
          if (startY > pageHeight - margin - 10) {
            addNewPage();
          }
          doc.text(noteLine, 35, startY + 4); // Slightly indented
          const isLastLine = idx === notesText.length - 1;
          startY += isLastLine ? lineSpacing : notesSpacing;
        });
        doc.setFontSize(12); // Reset font size for next todo
      }

      const isLastTodoForDate = dateIdx === dateTodos.length - 1;
      if (isLastTodoForDate) {
        startY += lineSpacing - 2; // Space between dates
      }
    });
  }

  // Save the PDF
  doc.save(`${downloadTitle}.pdf`);
};
